/**
 * components/emptyState.tsx
 * Premium, reusable empty state — a floating icon medallion layered with a
 * breathing glow, faint concentric rings, a slow rotating dashed ring, two
 * counter-orbiting dots, expanding halo pulses and twinkling sparkles, over a
 * headline, subtext and an optional call-to-action. All motion is web-only and
 * honours prefers-reduced-motion (keyframes live in web/index.html).
 *
 * Shared across the workspace list screens (Jobs, Candidates, …) so every empty
 * view feels identical and deliberate.
 */
import React from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../context/themeContext';
import { web } from '../utils/web';
import Icon from './icon';
import ActionButton from './actionButton';

/** Staggered fade + rise (web-only; inert on native). See @keyframes sk-rise. */
function rise(delay: number) {
  return web({ animation: 'sk-rise 460ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` });
}

/** Absolutely-centred layer inside the medallion (reliable across web/native). */
function centeredCircle(size: number) {
  return { position: 'absolute' as const, width: size, height: size, borderRadius: size / 2, top: '50%' as const, left: '50%' as const, marginTop: -size / 2, marginLeft: -size / 2 };
}

export type EmptyStateAction = { label: string; icon?: string; variant?: 'primary' | 'secondary'; onPress: () => void };

export interface EmptyStateProps {
  icon: string;
  title: string;
  subtitle: string;
  action?: EmptyStateAction;
  /** Entrance stagger offset (ms). */
  delay?: number;
}

export default function EmptyState({ icon, title, subtitle, action, delay = 60 }: EmptyStateProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const S = 172; // medallion canvas
  return (
    <View style={[{ paddingVertical: 56, alignItems: 'center' }, rise(delay)]}>
      <View style={[{ width: S, height: S, alignItems: 'center', justifyContent: 'center', marginBottom: 26 }, web({ animation: 'sk-pop 620ms cubic-bezier(.2,.8,.2,1) both', animationDelay: `${delay}ms` })]}>
        {/* Breathing radial glow. */}
        <View pointerEvents="none" style={[centeredCircle(150), { backgroundColor: colors.accentSoft }, web({ filter: 'blur(24px)', animation: 'sk-breathe 5s ease-in-out infinite' })]} />

        {/* Faint concentric rings — depth. */}
        <View pointerEvents="none" style={[centeredCircle(158), { borderWidth: 1, borderColor: colors.line, opacity: 0.45 }]} />
        <View pointerEvents="none" style={[centeredCircle(124), { borderWidth: 1, borderColor: colors.line, opacity: 0.8 }]} />

        {/* Expanding halo pulses. */}
        <View pointerEvents="none" style={[centeredCircle(100), { borderWidth: 1, borderColor: colors.lineStrong }, web({ animation: 'sk-halo 3.8s ease-out infinite' })]} />
        <View pointerEvents="none" style={[centeredCircle(100), { borderWidth: 1, borderColor: colors.lineStrong }, web({ animation: 'sk-halo 3.8s ease-out infinite', animationDelay: '1.9s' })]} />

        {/* Slow rotating dashed ring. */}
        <View pointerEvents="none" style={[centeredCircle(130), { borderWidth: 1.5, borderColor: colors.lineStrong, borderStyle: 'dashed', opacity: 0.7 }, web({ animation: 'sk-spin 24s linear infinite' })]} />

        {/* Two counter-orbiting dots (rotate a centred ring-sized track). */}
        <View pointerEvents="none" style={[centeredCircle(130), { alignItems: 'center' }, web({ animation: 'sk-spin 9s linear infinite' })]}>
          <View style={[{ width: 7, height: 7, borderRadius: 4, backgroundColor: colors.ink3, marginTop: -3.5 }, web({ boxShadow: `0 0 8px 0 ${colors.lineStrong}` })]} />
        </View>
        <View pointerEvents="none" style={[centeredCircle(158), { alignItems: 'center' }, web({ animation: 'sk-spin-rev 15s linear infinite' })]}>
          <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.ink4, marginTop: -2.5 }} />
        </View>

        {/* Twinkling sparkles. */}
        <View pointerEvents="none" style={[{ position: 'absolute', top: 20, right: 30 }, web({ animation: 'sk-twinkle 2.6s ease-in-out infinite' })]}>
          <Icon name="sparkles" size={13} color={colors.ink3} />
        </View>
        <View pointerEvents="none" style={[{ position: 'absolute', bottom: 26, left: 28 }, web({ animation: 'sk-twinkle 2.6s ease-in-out infinite', animationDelay: '1.3s' })]}>
          <Icon name="sparkles" size={9} color={colors.ink4} />
        </View>

        {/* Floating disc + inner ring highlight. */}
        <View
          style={[
            { width: 88, height: 88, borderRadius: 44, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line },
            web({ animation: 'sk-float 4.6s ease-in-out infinite', boxShadow: '0 20px 44px -20px rgba(0,0,0,0.55)' }),
          ]}
        >
          <View pointerEvents="none" style={[centeredCircle(72), { borderWidth: 1, borderColor: colors.line, opacity: 0.6 }]} />
          <Icon name={icon} size={31} color={colors.ink2} />
        </View>
      </View>
      <Text style={[{ fontSize: 18, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.3, textAlign: 'center' }, rise(delay + 110)]}>{title}</Text>
      <Text style={[{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 7, textAlign: 'center', maxWidth: 380, lineHeight: 20 }, rise(delay + 175)]}>{subtitle}</Text>
      {action && (
        <View style={[{ marginTop: 24 }, rise(delay + 250)]}>
          <ActionButton label={action.label} icon={action.icon} variant={action.variant ?? 'primary'} onPress={action.onPress} />
        </View>
      )}
    </View>
  );
}
